/* eslint-disable react/jsx-props-no-spreading */
import * as ReactDOMClient from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type FC, useCallback, useContext, useEffect, useRef } from 'react';
import { useCareerListingQuery, type CareerListingQuery } from 'cms/cms';
import CardCareer from '3-components/Cards/CardCareer/CardCareer';
import { resolveCareerListing } from 'cms/resolvers/careerListingResolver';
import CareerListingProvider from './CareerListingProvider';
import CareerListingContext, { type CareerListingContextProps, type CareerListingProps } from './CareerListingContext';
import { getQueryURL, getFilterQuery, getWhereValue, type WhereItem, getPageQueryURL } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { DEFAULT_FETCH_PARAMS, OPTIMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import { type ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';

const CareerListing: FC = () => {
    const {
        locale,
        activeFilters,
        setActiveFilters,
        listing: { filters, itemsPerPage = 4, filterByAncestors },
        page,
        setPage,
        label,
        cms,
    } = useContext(CareerListingContext) as CareerListingContextProps;

    // Destructure label fields for easy access
    const {
        title,
        clearFilterButton,
        loadingMessage,
        errorMessage,
        noCareerMessage,
        noCareerEmail,
    } = label;

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };

    const { search, yearpublished, ...restParams } = getFilterQuery(activeFilters, filters);
    const scrollToEl = useRef<HTMLDivElement>(null);

    const whereItems: WhereItem[] = [];
    if (search) {
        whereItems.push({
            type: 'search',
            field: '_fulltext',
            value: search,
        });
    }

    if (yearpublished) {
        whereItems.push({
            type: 'year',
            field: 'CareerPublishDate',
            value: yearpublished,
        });
    }

    if (filterByAncestors) {
        whereItems.push({
            type: 'ancestors',
            field: 'Ancestors',
            value: filterByAncestors,
        });
    }

    const where = getWhereValue(whereItems);

    const { data, error, isLoading, isError, isFetching } = useCareerListingQuery({ endpoint: `${cms?.apiUrl ?? OPTIMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`, fetchParams: DEFAULT_FETCH_PARAMS }, {
        locale,
        where,
        ...restParams,
        skip: page * itemsPerPage,
        limit: itemsPerPage,
    }, {

        queryKey: ['career-cards'],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        select: useCallback((result: CareerListingQuery) => {
            return resolveCareerListing(result);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []), // https://tkdodo.eu/blog/react-query-data-transformations
    });

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, filters));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    useEffect(() => {
        if (scrollToEl?.current) {
            scrollToEl.current.scrollIntoView({ behavior: 'smooth' });
        }

        window.history.replaceState({}, '', getPageQueryURL(page));
    }, [page]);

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={loadingMessage} />
        );
    }

    if (isError || !data) {
        console.error(error);

        return (
            <ListingError errorMessage={errorMessage} />
        );
    }

    return (
        <div className="listing-app" ref={scrollToEl}>
            <div className="container">
                {title && (
                    <ListingHeader
                        filters={filters}
                        activeFilters={activeFilters}
                        setActiveFilters={updateFilters}
                        title={title}
                        clearFilterLabel={clearFilterButton}
                    />
                )}
                <ListingContent
                    isFetching={isFetching}
                    isNoItems={!data.items.length}
                    pagination={{
                        itemsPerPage,
                        page,
                        setPage,
                        totalItems: data.total,
                    }}
                    loadingLabel={loadingMessage}
                    noItemsLabel={noCareerMessage}
                    noCareerEmail={noCareerEmail}
                    cols="1"

                >
                    {data.items.map(props => {
                        // eslint-disable-next-line no-console
                        return (
                            <CardCareer key={props.title} {...props} />
                        );
                    })}
                </ListingContent>
            </div>
        </div>
    );
};

const queryClient = new QueryClient();

interface CareerListingInitProps {
    el: HTMLDivElement;
    props: CareerListingProps;
}

const CareerListingInit = ({ el, props }: CareerListingInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <CareerListingProvider {...props}>
                <CareerListing />
            </CareerListingProvider>
        </QueryClientProvider>
    );
};

export default CareerListingInit;
